import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, apiError } from "../context/AuthContext";
import { btn, input, label } from "../lib/ui";
import { useI18n } from "../i18n";
import { AuthShell, googleLogin } from "./AuthShell";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mfa, setMfa] = useState(false);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const data = await login(email, password);
      if (data?.mfa_required) { setMfa(true); return; }
      toast.success(t("pub.login.welcome"));
      navigate("/dashboard");
    } catch (e) {
      setErr(apiError(e, t("pub.login.errFallback")));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await verifyMfa(code.trim());
      toast.success(t("pub.login.welcome"));
      navigate("/dashboard");
    } catch (e) {
      setErr(apiError(e, "Código no válido"));
    } finally {
      setBusy(false);
    }
  };

  if (mfa) {
    return (
      <AuthShell title="Verificación en dos pasos" subtitle="Introduce el código de 6 dígitos de tu app de autenticación (o un código de recuperación).">
        <form onSubmit={submitMfa} className="flex flex-col gap-4">
          <div>
            <label className={label} htmlFor="mfa-code">Código</label>
            <input id="mfa-code" data-testid="mfa-login-code" autoFocus value={code} onChange={(ev) => setCode(ev.target.value)} className={input} placeholder="123456" />
          </div>
          {err && <p data-testid="mfa-login-error" className="text-sm text-[#A85751]">{err}</p>}
          <button type="submit" data-testid="mfa-login-submit" disabled={busy} className={btn.accent + " w-full"}>{busy ? t("pub.login.submitting") : "Verificar"}</button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("pub.login.title")}
      subtitle={t("pub.login.subtitle")}
      footer={<>{t("pub.login.footerPre")}<Link to="/register" className="font-medium text-[#075E62] hover:text-[#0299A0]">{t("pub.login.footerLink")}</Link></>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <label className={label} htmlFor="email">{t("pub.login.email")}</label>
          <input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} placeholder={t("pub.login.emailPh")} />
        </div>
        <div>
          <label className={label} htmlFor="password">{t("pub.login.password")}</label>
          <input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={input} placeholder="••••••••" />
        </div>
        {err && <p data-testid="login-error" className="text-sm text-[#A85751]">{err}</p>}
        <button type="submit" data-testid="login-submit" disabled={busy} className={btn.accent + " w-full"}>{busy ? t("pub.login.submitting") : t("pub.login.submit")}</button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[#D5E7E5]" /><span className="text-xs text-[#5F8588]">{t("pub.login.or")}</span><div className="flex-1 h-px bg-[#D5E7E5]" />
      </div>
      <button onClick={googleLogin} data-testid="login-google" className={btn.outline + " w-full"}>{t("pub.login.google")}</button>

      <div className="mt-5 flex items-center justify-between text-sm">
        <Link to="/forgot-password" className="text-[#5F8588] hover:text-[#075E62]">{t("pub.login.forgot")}</Link>
        <Link to="/privacidad" data-testid="login-privacy-link" className="text-[#5F8588] hover:text-[#075E62]">{t("pub.login.privacy")}</Link>
      </div>
    </AuthShell>
  );
}
